import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, HelpCircle } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import BrandHeader from "@/components/brand/BrandHeader";

const HERO = "https://images.pexels.com/photos/32849133/pexels-photo-32849133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { user, login, clearSession } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (searchParams.get("cleared") === "1") {
      toast.success("Sessione pulita. Prova ad accedere di nuovo.");
      // rimuovi il flag dall'URL così non si riavvia il toast su reload
      const next = new URLSearchParams(searchParams);
      next.delete("cleared");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (user) {
    // R60.22: se c'è un post_login_redirect (es. da /manuale-haccp) usa quello
    const redirect = sessionStorage.getItem("post_login_redirect");
    if (redirect) {
      sessionStorage.removeItem("post_login_redirect");
      return <Navigate to={redirect} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) toast.error(r.error);
    else {
      toast.success("Bentornato");
      const redirect = sessionStorage.getItem("post_login_redirect");
      if (redirect) {
        sessionStorage.removeItem("post_login_redirect");
        nav(redirect);
      } else {
        nav("/");
      }
    }
  };

  const handleClearSession = async () => {
    if (clearing) return;
    setClearing(true);
    toast.info("Pulizia sessione in corso…");
    await clearSession(); // dopo questa, hard reload via window.location
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#12244A] via-[#1A2F5C] to-[#12244A] text-white relative overflow-hidden" data-testid="login-page">
      {/* Decorazioni radial gold */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 15% 15%, rgba(232,166,62,0.3) 0, transparent 45%), radial-gradient(circle at 85% 85%, rgba(232,166,62,0.2) 0, transparent 45%)" }} />

      <div className="relative min-h-screen flex flex-col">
        <BrandHeader variant="dark" showCta={false} />

        <main className="flex-1 flex items-center justify-center px-4 py-8">
          <div className="w-full max-w-md">
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-10">
              <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                <div>
                  <div className="w-12 h-1 bg-[#E8A63E] rounded-full mb-4" />
                  <h1 className="font-fraunces text-3xl font-bold tracking-tight text-[#12244A]" data-testid="login-title">
                    Bentornato
                  </h1>
                  <p className="text-slate-500 mt-1 text-sm">
                    Accedi al tuo account HACCP e-Log.
                  </p>

                  {/* Banner suspended / app-updated */}
                  {searchParams.get("reason") === "suspended" && (
                    <div className="mt-4 p-3 rounded-lg border border-rose-300 bg-rose-50 text-rose-900 text-xs" data-testid="login-suspended-banner">
                      <p className="font-semibold mb-0.5">Sessione interrotta</p>
                      <p className="leading-snug">
                        {(() => {
                          try { return sessionStorage.getItem("haccp_logout_reason") || "Il tuo account o la tua azienda risulta sospesa. Contatta il super-admin."; }
                          catch { return "Il tuo account o la tua azienda risulta sospesa. Contatta il super-admin."; }
                        })()}
                      </p>
                    </div>
                  )}
                  {searchParams.get("reason") === "app-updated" && (
                    <div className="mt-4 p-3 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 text-xs" data-testid="login-app-updated-banner">
                      <p className="font-semibold mb-0.5">🔄 Applicazione aggiornata</p>
                      <p className="leading-snug">
                        {(() => {
                          try { return sessionStorage.getItem("haccp_logout_reason") || "Abbiamo rilasciato un aggiornamento. Accedi di nuovo per vedere le novità."; }
                          catch { return "Abbiamo rilasciato un aggiornamento. Accedi di nuovo per vedere le novità."; }
                        })()}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <GoogleSignInButton intent="login" label="Continua con Google" />
                  <div className="relative flex items-center my-3">
                    <div className="flex-1 border-t border-slate-200" />
                    <span className="px-3 text-[10px] uppercase tracking-wider text-slate-400 font-bold">oppure email</span>
                    <div className="flex-1 border-t border-slate-200" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#12244A] text-xs font-semibold">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#12244A] text-xs font-semibold">Password</Label>
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11" data-testid="login-password-input" />
                </div>

                <Button type="submit" disabled={busy} className="w-full bg-[#E8A63E] hover:bg-[#D4941F] text-[#12244A] h-11 font-bold text-base" data-testid="login-submit-btn">
                  {busy ? "Accesso…" : "Accedi"}
                </Button>

                {/* R60.59 — accesso rapido operatore con PIN (per tablet della postazione) */}
                <Link
                  to="/entra"
                  className="w-full h-11 rounded-md border-2 border-dashed border-[#E8A63E]/70 text-[#12244A] font-bold flex items-center justify-center gap-2 hover:bg-[#FFF7E6] transition"
                  data-testid="login-staff-entry"
                >
                  🔑 Entra come operatore (PIN)
                </Link>

                <div className="flex items-center justify-between text-xs">
                  <Link to="/password-dimenticata" className="text-slate-600 hover:text-[#12244A] underline underline-offset-2" data-testid="login-forgot-password-link">
                    Password dimenticata?
                  </Link>
                  <Link to="/manuale-haccp" className="font-bold text-[#12244A] hover:text-[#E8A63E] underline underline-offset-2" data-testid="login-goto-signup">
                    Scopri i piani →
                  </Link>
                </div>

                {/* Trouble details */}
                <details className="rounded-lg border border-slate-200 bg-slate-50/70 text-xs" data-testid="login-trouble-banner">
                  <summary className="p-3 cursor-pointer font-semibold text-slate-700 flex items-center gap-2 hover:bg-slate-100 rounded-lg">
                    <HelpCircle className="w-4 h-4 shrink-0 text-slate-500" />
                    Problemi ad accedere?
                  </summary>
                  <div className="p-3 pt-0 space-y-2 text-slate-600">
                    <p>Se hai già usato l&apos;app in passato la sessione può essere obsoleta.</p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={handleClearSession}
                        disabled={clearing}
                        className="inline-flex items-center gap-1.5 text-[#12244A] font-semibold underline underline-offset-2 hover:text-[#E8A63E] disabled:opacity-50"
                        data-testid="login-clear-session-btn"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${clearing ? "animate-spin" : ""}`} />
                        {clearing ? "Pulizia…" : "Pulisci sessione"}
                      </button>
                      <Link to="/check-browser" className="text-[#12244A] font-semibold underline underline-offset-2 hover:text-[#E8A63E]" data-testid="login-check-browser-link">
                        Diagnostica browser
                      </Link>
                    </div>
                  </div>
                </details>
              </form>
            </div>

            <div className="mt-6 text-center text-xs text-white/60 space-y-1">
              <p>
                <ShieldCheck className="w-3 h-3 inline mr-1 text-[#E8A63E]" />
                Regolamento CE 852/2004 · Multi-tenant · v2.0
              </p>
              <p>
                <Link to="/installa" className="text-white/80 hover:text-[#E8A63E] underline underline-offset-2" data-testid="login-installa-link">
                  Installa l&apos;app sul tuo dispositivo →
                </Link>
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
